import { Box, Chip, Divider, Icon, IconButton, Menu, MenuItem, styled, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { EllipsisVerticalIcon, FileSearchIcon, SquarePenIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import caseItemImage from '../../../../assets/images/case-file-grid-bg.svg';
import { useCaseApi } from '../../../../services/api/useCase.api';
import { Case } from '../../../../types/Case/Case.type';
import DeleteCaseModal from '../DeleteCaseModal';
import RenameCaseModal from '../RenameCaseModal';

const CaseCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  height: '100%',
  minWidth: '240px',
  width: '240px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
}));

const CaseImageWrapper = styled('div')({
  background: '#f7fafc',
  objectFit: 'cover',
  borderRadius: '8px 8px 0 0',
  height: '140px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px 24px',
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'fit',
  },
});

const CaseDataWrapper = styled('div')({
  padding: '12px 0px 12px 12px',
  display: 'flex',
  flexDirection: 'column',
});

export const GridCaseItem = ({ caseData, activeSort }: { caseData: Case; activeSort: string }) => {
  const queryClient = useQueryClient();
  const { renameCase, deleteCase } = useCaseApi();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const menuOpen = Boolean(anchorEl);

  const { mutate: renameCaseMutation, isPending: isRenaming } = useMutation({
    mutationKey: ['renameCase'],
    mutationFn: ({ id, name }: { id: number; name: string }) => renameCase(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userCases'], exact: false });
      handleRenameModalClose();
    },
  });

  const { mutate: deleteCaseMutation, isPending: isDeleting } = useMutation({
    mutationKey: ['deleteCase'],
    mutationFn: (id: number) => deleteCase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userCases'], exact: false });
      handleDeleteModalClose();
    },
  });

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRenameOptionClick = () => {
    setRenameModalOpen(true);
    handleMenuClose();
  };

  const handleRenameModalClose = () => {
    setRenameModalOpen(false);
  };

  const handleRenameConfirm = (newName: string) => {
    if (caseData.id) {
      renameCaseMutation({ id: caseData.id, name: newName });
    }
  };

  const handleDeleteOptionClick = () => {
    setDeleteModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (caseData.id) {
      deleteCaseMutation(caseData.id);
    }
  };

  return (
    <CaseCard>
      <Link
        to={`/case-details/${caseData.id}`}
        style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column' }}
      >
        <CaseImageWrapper>
          {caseData.has_disparities_to_recalculate || (caseData.deleted_statute_names?.length ?? 0) > 0 ? (
            <Chip
              variant='outlined'
              size='small'
              label='Action Needed'
              sx={{
                position: 'absolute',
                backgroundColor: '#FFFAF0',
                borderColor: '#ED8936',
                color: '#ED8936',
                fontWeight: 600,
                top: '12px',
                right: '12px',
              }}
            />
          ) : null}
          <img src={caseItemImage} alt='' />
        </CaseImageWrapper>
        <Divider />
      </Link>
      <CaseDataWrapper>
        <Link
          to={`/case-details/${caseData.id}`}
          style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
        >
          <Typography
            variant='subtitle1'
            sx={{
              fontWeight: 600,
              color: '#2d3748',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              maxWidth: '208px',
            }}
            title={`${caseData.name} - ${caseData.statutes.map((statute) => statute.display_name).join(', ')}`}
          >
            {caseData.name} - {caseData.statutes.map((statute) => statute.display_name).join(', ')}
          </Typography>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link
            to={`/case-details/${caseData.id}`}
            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icon sx={{ color: 'secondary.main', display: 'flex', alignItems: 'center' }}>
                <FileSearchIcon style={{ width: '18px' }} />
              </Icon>
              {activeSort === 'created_at:desc' ? (
                <Typography
                  variant='caption'
                  sx={{
                    color: '#a0aec0',
                  }}
                >
                  Created:{' '}
                  {formatDistanceToNow(new Date(caseData.created_at), {
                    addSuffix: true,
                  })}
                </Typography>
              ) : (
                <Typography
                  variant='caption'
                  sx={{
                    color: '#a0aec0',
                  }}
                >
                  Last update:{' '}
                  {formatDistanceToNow(new Date(caseData.updated_at), {
                    addSuffix: true,
                  })}
                </Typography>
              )}
            </div>
          </Link>
          <IconButton
            aria-label='options'
            aria-haspopup='true'
            aria-controls={menuOpen ? 'options-menu' : undefined}
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={handleMenuClick}
            sx={{ color: 'primary.dark' }}
          >
            <EllipsisVerticalIcon />
          </IconButton>
          <Menu
            id='options-menu'
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            slotProps={{
              paper: {
                style: {
                  width: '20ch',
                },
              },
              list: { 'aria-labelledby': 'options-button' },
            }}
          >
            <MenuItem onClick={handleRenameOptionClick}>
              <SquarePenIcon style={{ marginRight: '16px', width: '20px' }} />
              Rename
            </MenuItem>
            <MenuItem onClick={handleDeleteOptionClick} sx={{ color: 'error.main' }}>
              <Trash2Icon style={{ marginRight: '16px', width: '20px' }} />
              Delete
            </MenuItem>
          </Menu>
          <RenameCaseModal
            open={renameModalOpen}
            onClose={handleRenameModalClose}
            currentCaseName={caseData.name}
            onRename={handleRenameConfirm}
            loading={isRenaming}
          />
          <DeleteCaseModal
            open={deleteModalOpen}
            onClose={handleDeleteModalClose}
            currentCaseName={caseData.name}
            onDelete={handleDeleteConfirm}
            loading={isDeleting}
          />
        </div>
      </CaseDataWrapper>
    </CaseCard>
  );
};
